// components/secure/ClientChecklistPanel.js — the checklist for one client.
//
// FormsPanel next door answers "which documents, in what order". This answers the
// question that comes before it: is this relationship in a state where we may act
// at all? So the loudest thing on the panel is not the progress count, it is the
// GATES — an outstanding item that refuses an offer or refuses marketing, shown
// with its citation, because that is a refusal the office needs to see rather than
// a task it can scroll past.
//
// Two behaviours are deliberate and will look like bugs if you do not know them:
//
//   * an item linked to a form has NO tick box. It closes when the form is signed.
//     The panel says so and points at Forms, because the way to close an agency
//     item is to get the agreement signed, not to tick it.
//   * the checklist does not appear until it is built. Generating asks the six
//     questions that decide which items apply, so a checklist never grows under
//     somebody while they are working on it.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from './Modal';

const PHASE_LABEL = {
    intake: 'Intake', engagement: 'Engagement', preparation: 'Preparation',
    offer_ready: 'Before an offer', ongoing: 'Ongoing',
};
const PHASE_ORDER = ['intake', 'engagement', 'preparation', 'offer_ready', 'ongoing'];

const STATUS_TONE = {
    open: 'bg-white',
    done: 'bg-green-50',
    waived: 'bg-slate-50',
    na: 'bg-slate-50',
};
const STATUS_PILL = {
    done: 'bg-green-100 text-green-800',
    waived: 'bg-slate-200 text-slate-600',
    na: 'bg-slate-200 text-slate-600',
    open: 'bg-amber-100 text-amber-800',
};
const STATUS_LABEL = { done: 'Done', waived: 'Waived', na: 'N/A', open: 'Open' };

const isOverdue = (item) => item.status === 'open' && item.due_on
    && new Date(item.due_on) < new Date(new Date().toDateString());

const ClientChecklistPanel = ({ clientId, transactions = [] }) => {
    const api = useApiClient();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(null);
    const [askProfile, setAskProfile] = useState(null);
    const [carryTo, setCarryTo] = useState('');

    const load = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            setData(await api.get(`/api/client-checklists/${clientId}`));
            setError('');
        } catch (e) {
            setError(e.message || 'Could not load the checklist.');
        } finally { setLoading(false); }
    }, [api, clientId]);
    useEffect(() => { load(); }, [load]);

    const items = useMemo(() => data?.items || [], [data]);
    const gates = data?.gates || { offer: [], marketing: [] };

    const grouped = useMemo(() => {
        const out = {};
        for (const it of items) {
            const phase = it.phase || 'intake';
            (out[phase] = out[phase] || []).push(it);
        }
        return PHASE_ORDER.filter((p) => out[p]?.length).map((p) => [p, out[p]]);
    }, [items]);

    const build = async (profile) => {
        setBusy('build'); setError(''); setNotice('');
        try {
            const res = await api.post(`/api/client-checklists/${clientId}/generate`, profile);
            setAskProfile(null);
            setNotice(res.raised
                ? `${res.raised} item${res.raised === 1 ? '' : 's'} added.`
                : 'The checklist is already up to date.');
            load();
        } catch (e) { setError(e.message); } finally { setBusy(null); }
    };

    const setStatus = async (item, status) => {
        setBusy(item.item_id); setError(''); setNotice('');
        try {
            await api.put(`/api/client-checklists/item/${item.item_id}`, { status });
            load();
        } catch (e) {
            // A 409 here is the feature working: the item belongs to a signature.
            setError(e.message);
        } finally { setBusy(null); }
    };

    const carry = async () => {
        if (!carryTo) return;
        setBusy('carry'); setError(''); setNotice('');
        try {
            const res = await api.post(`/api/client-checklists/${clientId}/carry/${carryTo}`);
            setNotice(res.carried
                ? `${res.carried} document${res.carried === 1 ? '' : 's'} carried into the transaction file.`
                : (res.skipped || 'Nothing to carry.'));
            load();
        } catch (e) { setError(e.message); } finally { setBusy(null); }
    };

    const openBuild = () => setAskProfile({
        asset_class: data?.profile?.asset_class || 'residential',
        transaction_type: data?.profile?.transaction_type || 'sale',
        built_pre_1978: !!data?.profile?.built_pre_1978,
        is_condo: !!data?.profile?.is_condo,
        financed: data?.profile?.financed == null ? true : !!data.profile.financed,
        dual_agency: !!data?.profile?.dual_agency,
    });

    if (loading && !data) {
        return <div className="bg-white p-8 rounded-lg shadow text-sm text-slate-500">Loading checklist…</div>;
    }

    const p = data?.progress || { total: 0, settled: 0, open: 0, overdue: 0 };
    const select = 'block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-slate-500 focus:border-slate-500';

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">Client checklist</h2>
                    <p className="text-sm text-slate-500">
                        What must be true about this client before there is a deal — and what carries
                        into the file when there is.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {p.total > 0 && (
                        <span className="text-sm text-slate-600">
                            {p.settled} of {p.total} settled
                            {p.overdue > 0 && <span className="text-red-600 font-medium"> · {p.overdue} overdue</span>}
                        </span>
                    )}
                    <button onClick={openBuild} disabled={busy === 'build'}
                        className="text-sm px-3 py-1.5 bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-indigo-300">
                        {busy === 'build' ? 'Building…' : (p.total ? 'Review applicability' : 'Build checklist')}
                    </button>
                </div>
            </div>

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            {notice && <p className="mt-3 text-sm text-green-700">{notice}</p>}

            {/* The gates come first, because a refusal is not a task. */}
            {(gates.offer.length > 0 || gates.marketing.length > 0) && (
                <div className="mt-4 space-y-2">
                    {gates.marketing.length > 0 && (
                        <div className="border border-red-200 bg-red-50 rounded-md p-3">
                            <p className="text-sm font-semibold text-red-800">
                                This property may not be marketed yet
                            </p>
                            <ul className="mt-1 space-y-0.5">
                                {gates.marketing.map((g) => (
                                    <li key={g.item_key} className="text-sm text-red-700">
                                        {g.title}{g.statutory_ref ? <span className="text-red-600/70"> — {g.statutory_ref}</span> : null}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {gates.offer.length > 0 && (
                        <div className="border border-amber-200 bg-amber-50 rounded-md p-3">
                            <p className="text-sm font-semibold text-amber-900">
                                No offer should be written for this client yet
                            </p>
                            <ul className="mt-1 space-y-0.5">
                                {gates.offer.map((g) => (
                                    <li key={g.item_key} className="text-sm text-amber-800">
                                        {g.title}{g.statutory_ref ? <span className="text-amber-700/70"> — {g.statutory_ref}</span> : null}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {p.total === 0 && (
                <p className="mt-6 text-sm text-slate-500">
                    No checklist yet. Build one to raise the items this client
                    {data?.party ? ` — as a ${data.party} — ` : ' '}needs.
                </p>
            )}

            {grouped.map(([phase, list]) => (
                <div key={phase} className="mt-5">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                        {PHASE_LABEL[phase] || phase}
                    </h3>
                    <div className="space-y-1.5">
                        {list.map((it) => (
                            <div key={it.item_id}
                                className={`border rounded-md p-3 ${STATUS_TONE[it.status] || 'bg-white'} ${
                                    isOverdue(it) ? 'border-red-300' : 'border-slate-200'}`}>
                                <div className="flex items-start gap-3">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-medium text-slate-800">{it.title}</span>
                                            <span className={`px-2 py-0.5 rounded text-xs ${STATUS_PILL[it.status]}`}>
                                                {STATUS_LABEL[it.status] || it.status}
                                            </span>
                                            {it.blocks !== 'none' && it.status === 'open' && (
                                                <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">
                                                    blocks {it.blocks === 'offer' ? 'an offer' : 'marketing'}
                                                </span>
                                            )}
                                            {it.satisfied_by === 'form' && (
                                                <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                                                    closed by signature
                                                </span>
                                            )}
                                            {it.carried_transaction_id && (
                                                <span className="px-2 py-0.5 rounded text-xs bg-indigo-100 text-indigo-800">
                                                    carried to deal #{it.carried_transaction_id}
                                                </span>
                                            )}
                                        </div>
                                        {it.detail && <p className="text-xs text-slate-500 mt-1">{it.detail}</p>}
                                        {it.statutory_ref && <p className="text-xs text-slate-400 mt-0.5">{it.statutory_ref}</p>}
                                        {it.due_on && it.status === 'open' && (
                                            <p className={`text-xs mt-0.5 ${isOverdue(it) ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                                                Due {new Date(it.due_on).toLocaleDateString()}
                                                {isOverdue(it) ? ' — overdue' : ''}
                                            </p>
                                        )}
                                        {it.form_key && it.status !== 'done' && (
                                            <p className="text-xs text-blue-700 mt-1">
                                                Closes when <span className="font-medium">{it.form_key.replace(/_/g, ' ')}</span> is
                                                signed — generate it in Forms below.
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {/* No tick box for an item a signature owns. */}
                                        {!it.form_key && it.status !== 'done' && (
                                            <button onClick={() => setStatus(it, 'done')} disabled={busy === it.item_id}
                                                className="text-xs px-2 py-1 border border-green-300 text-green-700 rounded hover:bg-green-50 disabled:opacity-50">
                                                Mark done
                                            </button>
                                        )}
                                        {it.status === 'open' && (
                                            <>
                                                <button onClick={() => setStatus(it, 'waived')} disabled={busy === it.item_id}
                                                    className="text-xs px-2 py-1 border border-slate-300 text-slate-600 rounded hover:bg-slate-50 disabled:opacity-50">
                                                    Waive
                                                </button>
                                                <button onClick={() => setStatus(it, 'na')} disabled={busy === it.item_id}
                                                    className="text-xs px-2 py-1 border border-slate-300 text-slate-600 rounded hover:bg-slate-50 disabled:opacity-50">
                                                    N/A
                                                </button>
                                            </>
                                        )}
                                        {it.status !== 'open' && it.satisfied_by !== 'form' && (
                                            <button onClick={() => setStatus(it, 'open')} disabled={busy === it.item_id}
                                                className="text-xs px-2 py-1 border border-slate-300 text-slate-600 rounded hover:bg-slate-50 disabled:opacity-50">
                                                Reopen
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {data?.missing?.length > 0 && (
                <p className="mt-4 text-xs text-slate-500">
                    {data.missing.length} further item{data.missing.length === 1 ? '' : 's'} in the
                    catalogue would apply to this client. Use <em>Review applicability</em> to raise them.
                </p>
            )}

            {/* Carrying into a deal */}
            {p.total > 0 && transactions.length > 0 && (
                <div className="mt-6 pt-4 border-t border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-800">Carry into a transaction file</h3>
                    <p className="text-xs text-slate-500 mt-0.5 mb-2">
                        Document-shaped items become required documents on the deal, keeping the date they
                        were actually collected — so a disclosure gathered at intake is not asked for twice.
                        Items still open carry as gaps.
                    </p>
                    <div className="flex items-center gap-2">
                        <select className={`${select} max-w-sm`} value={carryTo}
                            onChange={(e) => setCarryTo(e.target.value)}>
                            <option value="">Choose a transaction…</option>
                            {transactions.map((t) => (
                                <option key={t.transaction_id} value={t.transaction_id}>
                                    #{t.transaction_id} — {t.street_address || t.transaction_type || 'transaction'}
                                </option>
                            ))}
                        </select>
                        <button onClick={carry} disabled={!carryTo || busy === 'carry'}
                            className="text-sm px-3 py-2 border border-indigo-300 text-indigo-700 rounded-md hover:bg-indigo-50 disabled:opacity-50">
                            {busy === 'carry' ? 'Carrying…' : 'Carry'}
                        </button>
                    </div>
                </div>
            )}

            {/* Applicability questions */}
            {askProfile && (
                <Modal open size="md" title="Which items apply to this client?"
                    onClose={() => setAskProfile(null)}
                    footer={<>
                        <button onClick={() => setAskProfile(null)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
                        <button onClick={() => build(askProfile)} disabled={busy === 'build'}
                            className="px-4 py-2 text-sm bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-indigo-300">
                            {busy === 'build' ? 'Building…' : 'Build checklist'}
                        </button>
                    </>}>
                    <p className="text-sm text-slate-600 mb-4">
                        These answers decide which statutory items are raised. They are remembered, so
                        rebuilding later is consistent. Items already settled are never removed or reset.
                    </p>
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Asset class</label>
                            <select className={select} value={askProfile.asset_class}
                                onChange={(e) => setAskProfile({ ...askProfile, asset_class: e.target.value })}>
                                <option value="residential">Residential</option>
                                <option value="commercial">Commercial</option>
                                <option value="industrial">Industrial</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Sale or lease</label>
                            <select className={select} value={askProfile.transaction_type}
                                onChange={(e) => setAskProfile({ ...askProfile, transaction_type: e.target.value })}>
                                <option value="sale">Sale</option>
                                <option value="lease">Lease</option>
                                <option value="both">Both</option>
                            </select>
                        </div>
                        {[
                            ['built_pre_1978', 'Built before 1978', 'Adds the federal lead-based paint disclosure and its ten-day inspection opportunity.'],
                            ['is_condo', 'Condominium', 'Adds the 22.1 resale disclosures, which the association takes weeks to produce.'],
                            ['financed', 'Financed rather than cash', 'Adds the lender identification item.'],
                            ['dual_agency', 'The firm represents both sides', 'Adds written consent to dual agency.'],
                        ].map(([key, label, why]) => (
                            <label key={key} className="flex gap-3 items-start cursor-pointer">
                                <input type="checkbox" className="mt-1 h-4 w-4" checked={!!askProfile[key]}
                                    onChange={(e) => setAskProfile({ ...askProfile, [key]: e.target.checked })} />
                                <span>
                                    <span className="text-sm font-medium text-slate-800">{label}</span>
                                    <span className="block text-xs text-slate-500">{why}</span>
                                </span>
                            </label>
                        ))}
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ClientChecklistPanel;
